import {
  hasRole,
  type AgentDto,
  type Role,
  type SessionDetailDto,
  type SessionDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Eraser, Layers, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageAttachments } from '@/components/attachments';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Markdown } from '@/components/markdown';
import { api, errorMessage } from '@/lib/api';
import { formatCompact, formatDateTime, formatRelative } from '@/lib/format';
import { usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';

const ALL = '__all';
const keyLabel = (s: Pick<SessionDto, 'key'>) => (s.key === '*' ? 'everyone' : s.key);

function SessionPanel({
  tenantId,
  sessionId,
  canEdit,
  onClose,
}: {
  tenantId: string;
  sessionId: string | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['session', tenantId, sessionId],
    queryFn: () => api<SessionDetailDto>(`/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
  });
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (detail.data) setNotes(detail.data.notes);
  }, [detail.data]);
  const [confirm, setConfirm] = useState<'clear' | 'delete' | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['sessions', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['session', tenantId, sessionId] });
  };
  const saveNotes = useMutation({
    mutationFn: () =>
      api<SessionDto>(`/sessions/${sessionId}`, { method: 'PATCH', body: { notes } }),
    onSuccess: () => {
      toast.success('Notes saved');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const clear = useMutation({
    mutationFn: () => api<void>(`/sessions/${sessionId}/messages`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Conversation cleared');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/sessions/${sessionId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Session deleted');
      void queryClient.invalidateQueries({ queryKey: ['sessions', tenantId] });
      onClose();
    },
  });
  const s = detail.data;

  return (
    <Sheet open={Boolean(sessionId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Layers className="size-4" aria-hidden />
            {s ? (
              <>
                {s.level}
                <span className="text-muted-foreground font-mono text-sm">{keyLabel(s)}</span>
              </>
            ) : (
              'Session context'
            )}
          </SheetTitle>
          <SheetDescription>
            {s
              ? `${s.agentName} · ${s.messageCount} messages · ~${formatCompact(s.approxTokens)} tokens · active ${formatRelative(s.lastActiveAt)}`
              : 'Loading…'}
          </SheetDescription>
        </SheetHeader>
        <div className="grid min-h-0 flex-1 content-start gap-5 overflow-y-auto px-4 pb-4">
          <ErrorAlert error={detail.error} />
          {!s ? (
            <Skeleton className="h-40" />
          ) : (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="session-notes">Shared notes</Label>
                <Textarea
                  id="session-notes"
                  className="min-h-28 text-sm"
                  value={notes}
                  disabled={!canEdit}
                  placeholder="Nothing remembered at this level yet."
                  onChange={(e) => setNotes(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  Everyone who shares this level sees these notes in the agent’s instructions; the
                  agent updates them with <code>update_session_memory</code>.
                </p>
                {canEdit && (
                  <Button
                    size="sm"
                    className="justify-self-start"
                    disabled={notes === s.notes || saveNotes.isPending}
                    onClick={() => saveNotes.mutate()}
                  >
                    {saveNotes.isPending ? 'Saving…' : 'Save notes'}
                  </Button>
                )}
              </div>

              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Message history</span>
                  {canEdit && s.messageCount > 0 && (
                    <Button size="sm" variant="destructive" onClick={() => setConfirm('clear')}>
                      <Eraser aria-hidden /> Clear
                    </Button>
                  )}
                </div>
                {s.messages.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No messages. Only the most specific level a request identifies keeps the
                    conversation.
                  </p>
                ) : (
                  <ol className="grid gap-3" aria-label="Messages">
                    {s.messages.map((m) => (
                      <li
                        key={m.id}
                        className={cn(
                          'grid max-w-[90%] gap-1.5',
                          m.role === 'user' ? 'justify-items-end justify-self-end' : '',
                        )}
                      >
                        {m.attachments.length > 0 && (
                          <MessageAttachments attachments={m.attachments} />
                        )}
                        {m.content &&
                          (m.role === 'user' ? (
                            <div className="bg-accent rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap">
                              {m.content}
                            </div>
                          ) : (
                            <Markdown className="bg-muted/60 rounded-2xl px-3 py-2">
                              {m.content}
                            </Markdown>
                          ))}
                        <span className="text-muted-foreground text-[11px]">
                          {formatDateTime(m.createdAt)}
                          {m.runId && (
                            <>
                              {' · '}
                              <Link
                                to="/traces/runs/$runId"
                                params={{ runId: m.runId }}
                                className="hover:text-foreground font-mono"
                              >
                                run {m.runId.slice(-8)}
                              </Link>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              {canEdit && (
                <Button
                  variant="destructive"
                  className="justify-self-start"
                  onClick={() => setConfirm('delete')}
                >
                  Delete session
                </Button>
              )}
            </>
          )}
        </div>
        <ConfirmDeleteDialog
          open={confirm === 'clear'}
          onOpenChange={(open) => !open && setConfirm(null)}
          title="Clear this conversation?"
          description="The messages are deleted; the notes stay. The next turn starts a fresh conversation."
          confirmLabel="Clear"
          onConfirm={() => clear.mutateAsync()}
        />
        <ConfirmDeleteDialog
          open={confirm === 'delete'}
          onOpenChange={(open) => !open && setConfirm(null)}
          title="Delete this session?"
          description="Its notes and conversation are deleted. It is created again, empty, the next time a request identifies it."
          onConfirm={() => remove.mutateAsync()}
        />
      </SheetContent>
    </Sheet>
  );
}

function SessionsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const [agentId, setAgentId] = useState<string>(ALL);
  const [level, setLevel] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams();
  if (agentId !== ALL) params.set('agentId', agentId);
  if (/^[a-z][a-z0-9_]{0,31}$/.test(level)) params.set('level', level);
  if (debounced) params.set('q', debounced);
  const query = params.toString();
  const sessions = usePaged<SessionDto>(
    ['sessions', tenantId, query],
    `/sessions${query ? `?${query}` : ''}`,
    canEdit,
  );
  const rows = sessions.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const agentList = agents.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Session context"
        description={
          <>
            The context agents in <em>Sessions</em> memory mode carry across conversations, one
            session per level and key (e.g. global, store, user): shared notes plus that session's
            message history. To read a conversation turn by turn with its traces, open{' '}
            <Link to="/traces" search={{ tab: 'threads' }} className="underline">
              Traces → Threads
            </Link>
            .
          </>
        }
      />
      {!canEdit ? (
        <EmptyState title="Admins only">
          Session context holds end users’ conversations, so it needs the admin role.
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sessions-agent">Agent</Label>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger id="sessions-agent" className="w-56" aria-label="Agent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All agents</SelectItem>
                  {agentList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sessions-level">Level</Label>
              <Input
                id="sessions-level"
                className="w-36 font-mono"
                placeholder="any"
                value={level}
                onChange={(e) => setLevel(e.target.value.toLowerCase())}
              />
            </div>
            <div className="relative grid gap-1.5">
              <Label htmlFor="sessions-q">Session key</Label>
              <Search
                className="text-muted-foreground pointer-events-none absolute bottom-2 left-3 size-4"
                aria-hidden
              />
              <Input
                id="sessions-q"
                type="search"
                className="w-64 pl-9"
                placeholder="Search keys (e.g. a user id)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          <ErrorAlert error={sessions.error} />
          {sessions.isPending ? (
            <Skeleton className="h-40" />
          ) : rows.length === 0 ? (
            <EmptyState title={query ? 'No matching sessions' : 'No session context yet'}>
              Set an agent’s memory to <strong>Sessions</strong> in its editor. Each session’s
              context appears here once a request identifies it.
            </EmptyState>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Level</TableHead>
                      <TableHead>Session key</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Shared notes</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Last active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((s) => (
                      <TableRow
                        key={s.id}
                        className="cursor-pointer"
                        data-testid="session-row"
                        onClick={() => setOpen(s.id)}
                      >
                        <TableCell>
                          <Badge variant="secondary" className="font-mono">
                            {s.level}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-48 truncate font-mono text-xs">
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(s.id);
                            }}
                          >
                            {keyLabel(s)}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm">{s.agentName}</TableCell>
                        <TableCell className="text-muted-foreground max-w-64 truncate text-xs">
                          {s.notes || '—'}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">
                          {s.messageCount}
                          {s.approxTokens > 0 && (
                            <span className="text-muted-foreground">
                              {' '}
                              · ~{formatCompact(s.approxTokens)} tok
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-xs">
                          {formatRelative(s.lastActiveAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {sessions.hasNextPage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    disabled={sessions.isFetchingNextPage}
                    onClick={() => void sessions.fetchNextPage()}
                  >
                    Load more
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
      <SessionPanel
        tenantId={tenantId}
        sessionId={open}
        canEdit={canEdit}
        onClose={() => setOpen(null)}
      />
    </>
  );
}

export function SessionsPage() {
  return (
    <RequireTenant>
      {(tenant) => <SessionsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
